import { createContext, ReactNode, useCallback, useContext, useRef } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { useRegisterJumpMarkSection } from "src/components-mui/common/jump-marks/jump-marks.context";
import { toSxArray } from "src/utils/mui/sx/sx";

const DATA_ATTR_JUMPMARK_SECTION = "data-jumpmark";

const EnclosingJumpMarkSectionContext = createContext<string | undefined>(undefined);

export const useEnclosingJumpMarkSection = () => useContext(EnclosingJumpMarkSectionContext);

interface IJumpMarkSectionProps {
    section: string;
    anchor?: boolean;
    children?: ReactNode;
    sx?: SxProps<Theme>;
}

export const JumpMarkSection = ({ section, anchor = false, children, sx }: IJumpMarkSectionProps) => {
    const registerSection = useRegisterJumpMarkSection();
    const unregisterRef = useRef<() => void>();

    const sectionRef = useCallback(
        (element: HTMLDivElement | null) => {
            unregisterRef.current?.();
            unregisterRef.current = element ? registerSection({ id: section, element, isAnchor: anchor }) : undefined;
        },
        [anchor, registerSection, section]
    );

    return (
        <EnclosingJumpMarkSectionContext.Provider value={section}>
            <Box
                ref={sectionRef}
                id={anchor ? section : undefined}
                {...{ [DATA_ATTR_JUMPMARK_SECTION]: section }}
                sx={[{ "&:focus": { outline: "none" } }, ...toSxArray(sx)]}
            >
                {children}
            </Box>
        </EnclosingJumpMarkSectionContext.Provider>
    );
};

interface IJumpMarkSharedSectionProps {
    section: string;
    sharedWith: readonly string[];
    children: ReactNode;
}

export const JumpMarkSharedSection = ({ section, sharedWith, children }: IJumpMarkSharedSectionProps) => (
    <>
        {sharedWith.map(sharedSection => (
            <JumpMarkSection key={sharedSection} section={sharedSection} anchor />
        ))}
        <JumpMarkSection section={section} anchor>
            {children}
        </JumpMarkSection>
    </>
);
